import { apiClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';

import { toListQuery } from './filters';
import { productsReportListResponseSchema } from './schemas';
import type { ProductsReportListParams, ProductsReportListResponse } from './types';

/** DataTable column key → the backend's `sortBy` token. Only one differs: the Customer column
 *  renders `row.customerName`, so antd reports `customerName`, but the DTO's enum spells it
 *  `customer`. The other five column keys are the wire tokens verbatim. */
const SORT_BY: Record<string, string> = { customerName: 'customer' };

/** Sort + filters → the list's wire params, defaults included. The export sends the same, so the
 *  file comes out with the rows and order on screen, sorted or not (ADO 120974). */
export function productsRequestQuery(params: Omit<ProductsReportListParams, 'page' | 'limit'>) {
  const { sortBy = 'createdDate', sortOrder = 'desc', ...filters } = params;
  return {
    sortBy: SORT_BY[sortBy] ?? sortBy,
    sortOrder: sortOrder.toUpperCase(),
    // The whole filter set in one mapping — search included. `toListQuery` joins the
    // multi-selects to comma strings and drops empty ones, so an unselected filter is an
    // ABSENT param rather than a blank one. Ticket 04's hand-picked `search: filters.search`
    // is replaced here: a rest-spread that forwards one field silently drops the rest, which
    // was flagged in 04's review as the exact hazard this ticket would hit.
    ...toListQuery(filters),
  };
}

/** Paginated/sorted/searchable Products report — **one row per owned unit**, devices and
 *  components alike, never aggregated per Customer or per Order (AC 112361).
 *
 *  Defaults to `createdDate DESC`: this tab has no date column, so "newest first" (112312) anchors
 *  on the unit's provisioning date, which is its order date (grilling Q10). A consequence to
 *  expect, not to fix: no column header loads in an active sorted state.
 *
 *  `sortOrder` is uppercased for the DTO's `SortOrder` enum, as on every sibling listing. */
export async function listProductsReport(
  params: ProductsReportListParams,
): Promise<ProductsReportListResponse> {
  const { page, limit, ...rest } = params;
  const { data, meta } = await apiClient.getList(endpoints.reports.products, {
    params: { page, limit, ...productsRequestQuery(rest) },
  });
  return productsReportListResponseSchema.parse({ data, meta });
}
